import { IconChevronDownSmall } from '@hubble/icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { createContext, forwardRef, useState, useRef, useEffect, useMemo, useLayoutEffect, useContext } from 'react';
import { useId } from 'react-id-generator';
import { Message } from '../../forms/Message/index.js';
import { extractElementFromChildren, filterOutElements, getDropdownAlignOffset } from '../shared/utils.js';
import { SelectWrapper, FloatingLabel, Trigger, TriggerContent, Content, Viewport, Item, ItemText, LeftElementWrapper, RightElementWrapper } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { Spacer, cssShorthandPropsFilter } from '../../primitives/Spacer/index.js';

const SelectContext = createContext(null);
const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('Select components must be used within a Select.Root');
  }
  return context;
};
const Root = forwardRef(({
  'data-testid': dataTestId,
  className,
  disabled,
  error,
  id: providedId,
  inputSize = 'md',
  message,
  name,
  successMessage,
  value,
  defaultValue,
  onValueChange,
  open,
  children,
  ...rest
}, ref) => {
  const [uniqueId] = useId(1, 'select-');
  const id = providedId || uniqueId;
  const [isFocused, setIsFocused] = useState(false);
  const isControlled = useRef(value !== undefined).current;
  const [hasValue, setHasValue] = useState(Boolean(value !== null && value !== void 0 ? value : defaultValue));
  useEffect(() => {
    if (isControlled) setHasValue(Boolean(value));
  }, [isControlled, value]);
  const {
    leftElement,
    rightElement,
    hasLeftElement,
    hasRightElement,
    hasMessage
  } = useMemo(() => {
    const left = extractElementFromChildren(children, LeftElement);
    const right = extractElementFromChildren(children, RightElement);
    return {
      leftElement: left,
      rightElement: right,
      hasLeftElement: Boolean(left),
      hasRightElement: Boolean(right),
      hasMessage: Boolean(message || error || successMessage)
    };
  }, [children, message, error, successMessage]);
  const wrapperRef = useRef(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) {
        setWrapperWidth(wrapperRef.current.getBoundingClientRect().width);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  const contextValue = useMemo(() => ({
    id,
    inputSize,
    disabled,
    error,
    successMessage,
    isFocused,
    hasValue,
    hasLeftElement,
    hasRightElement,
    leftElement,
    rightElement,
    wrapperWidth,
    setIsFocused,
    setHasValue
  }), [id, inputSize, disabled, error, successMessage, isFocused, hasValue, hasLeftElement, hasRightElement, leftElement, rightElement, wrapperWidth]);
  const handleValueChange = newValue => {
    setHasValue(Boolean(newValue));
    onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(newValue);
  };
  const selectProps = isControlled ? {
    value,
    onValueChange: handleValueChange
  } : {
    defaultValue,
    onValueChange: handleValueChange
  };
  return jsx(SelectContext.Provider, {
    value: contextValue,
    children: jsx(SelectPrimitive.Root, {
      ...selectProps,
      disabled: disabled,
      open: open,
      name: name,
      children: jsxs(Spacer, {
        ref: ref,
        "data-testid": dataTestId,
        className: className,
        ...cssShorthandPropsFilter(rest),
        children: [jsx(SelectWrapper, {
          ref: wrapperRef,
          hasError: Boolean(error),
          inputSize: inputSize,
          isDisabled: disabled,
          isSuccessful: Boolean(successMessage),
          isFocused: isFocused,
          hasValue: hasValue,
          "data-left-element": hasLeftElement || undefined,
          children: filterOutElements(children, LeftElement, RightElement)
        }), hasMessage && jsx(Message, {
          "data-testid": `${dataTestId}-message`,
          disabled: disabled,
          error: error,
          id: `${id}-message`,
          message: message,
          successMessage: successMessage
        })]
      })
    })
  });
});
const Label = forwardRef(({
  children
}, ref) => {
  const {
    id,
    inputSize,
    isFocused,
    hasValue,
    disabled,
    error,
    successMessage,
    hasLeftElement
  } = useSelectContext();
  return jsx(FloatingLabel, {
    ref: ref,
    htmlFor: id,
    inputSize: inputSize,
    isFocused: isFocused,
    hasValue: hasValue,
    isDisabled: disabled,
    hasError: Boolean(error),
    isSuccessful: Boolean(successMessage),
    hasLeftElement: hasLeftElement,
    children: children
  });
});
const SelectTrigger = forwardRef(({
  children,
  ...props
}, ref) => {
  const {
    id,
    disabled,
    hasRightElement,
    leftElement,
    rightElement,
    setIsFocused
  } = useSelectContext();
  return jsxs(Trigger, {
    ref: ref,
    id: id,
    disabled: disabled,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    "data-main-content": true,
    ...props,
    children: [leftElement, jsx(TriggerContent, {
      children: children
    }), rightElement || !hasRightElement && jsx(RightElement, {
      children: jsx(IconChevronDownSmall, {})
    })]
  });
});
const Value = forwardRef(({
  placeholder,
  ...props
}, ref) => {
  const {
    hasValue,
    isFocused
  } = useSelectContext();
  return jsx(SelectPrimitive.Value, {
    ref: ref,
    placeholder: isFocused ? placeholder : undefined,
    style: {
      opacity: !hasValue && !isFocused ? 0 : 1,
      transition: 'opacity 0.2s ease'
    },
    ...props
  });
});
const SelectContent = forwardRef(({
  children,
  ...props
}, ref) => {
  const {
    wrapperWidth,
    inputSize
  } = useSelectContext();
  return jsx(SelectPrimitive.Portal, {
    children: jsx(Content, {
      ref: ref,
      position: "popper",
      sideOffset: 10,
      alignOffset: getDropdownAlignOffset(inputSize),
      wrapperWidth: wrapperWidth,
      ...props,
      children: jsx(Viewport, {
        children: children
      })
    })
  });
});
const SelectItem = forwardRef(({
  children,
  ...props
}, ref) => jsx(Item, {
  ref: ref,
  ...props,
  children: jsx(ItemText, {
    children: children
  })
}));
const LeftElement = forwardRef(({
  children
}, ref) => {
  const {
    inputSize
  } = useSelectContext();
  return jsx(LeftElementWrapper, {
    ref: ref,
    inputSize: inputSize,
    children: children
  });
});
const RightElement = forwardRef(({
  children
}, ref) => {
  const {
    inputSize
  } = useSelectContext();
  return jsx(RightElementWrapper, {
    ref: ref,
    inputSize: inputSize,
    children: children
  });
});
Root.displayName = 'Select.Root';
Label.displayName = 'Select.Label';
SelectTrigger.displayName = 'Select.Trigger';
Value.displayName = 'Select.Value';
SelectContent.displayName = 'Select.Content';
SelectItem.displayName = 'Select.Item';
LeftElement.displayName = 'Select.LeftElement';
RightElement.displayName = 'Select.RightElement';
const ComposableSelect = Object.assign(Root, {
  Label,
  Trigger: SelectTrigger,
  Value,
  Content: SelectContent,
  Item: SelectItem,
  LeftElement,
  RightElement,
  Group: SelectPrimitive.Group,
  Separator: SelectPrimitive.Separator,
  ScrollUpButton: SelectPrimitive.ScrollUpButton,
  ScrollDownButton: SelectPrimitive.ScrollDownButton
});

export { ComposableSelect };
